import type { PlanTier } from '../types'
import { getToastErrorMessage, useToast } from './Toast'

interface NotificationSettingsProps {
  permission: NotificationPermission
  prefs: { push_enabled: boolean; whatsapp_notify: boolean; days_before: number }
  planTier: PlanTier
  hasWhatsApp: boolean
  onRequestPermission: () => Promise<void>
  onTogglePush: (enabled: boolean) => Promise<void>
  onSaveDaysBefore: (days: number) => Promise<void>
  onToggleWhatsApp: (enabled: boolean) => Promise<void>
}

export function NotificationSettings({
  permission,
  prefs,
  planTier,
  hasWhatsApp,
  onRequestPermission,
  onTogglePush,
  onSaveDaysBefore,
  onToggleWhatsApp,
}: NotificationSettingsProps) {
  const toast = useToast()

  async function handleRequestPermission() {
    try {
      await onRequestPermission()
      toast.success('Notificações ativadas.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao ativar notificações.'))
    }
  }

  async function handleTogglePush(enabled: boolean) {
    try {
      await onTogglePush(enabled)
      toast.success(enabled ? 'Notificações push ativadas.' : 'Notificações push desativadas.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar notificação push.'))
    }
  }

  async function handleSaveDaysBefore(days: number) {
    try {
      await onSaveDaysBefore(days)
      toast.success('Antecedência salva.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar antecedência.'))
    }
  }

  async function handleToggleWhatsApp(enabled: boolean) {
    try {
      await onToggleWhatsApp(enabled)
      toast.success(enabled ? 'Alertas no WhatsApp ativados.' : 'Alertas no WhatsApp desativados.')
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao salvar alertas no WhatsApp.'))
    }
  }

  if (planTier === 'free') {
    return <p className="settings-note">Notificações disponíveis nos planos Pro e Casal.</p>
  }

  return (
    <div className="bc-account-settings__card">
      <div className="bc-account-settings__item-title">Notificações de Vencimento</div>

      {permission === 'default' && (
        <button className="bc-btn bc-btn--primary" onClick={handleRequestPermission} style={{ marginTop: '0.5rem' }}>
          Ativar notificações
        </button>
      )}
      {permission === 'denied' && (
        <p className="bc-account-settings__error" style={{ marginTop: '0.5rem' }}>
          Notificações bloqueadas. Reative nas configurações do navegador.
        </p>
      )}
      {permission === 'granted' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
          <div className="bc-account-settings__row">
            <label className="bc-account-settings__item-desc">Notificações push</label>
            <input
              type="checkbox"
              role="switch"
              aria-label="Notificações"
              checked={prefs.push_enabled}
              onChange={e => handleTogglePush(e.target.checked)}
            />
          </div>
          {prefs.push_enabled && (
            <div className="bc-account-settings__row">
              <label htmlFor="days-before" className="bc-account-settings__item-desc">Avisar com antecedência</label>
              <select
                id="days-before"
                aria-label="Dias de antecedência"
                value={prefs.days_before}
                onChange={e => handleSaveDaysBefore(Number(e.target.value))}
                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--sand-4)' }}
              >
                {[1, 2, 3, 5, 7].map(d => (
                  <option key={d} value={d}>{d} dia{d > 1 ? 's' : ''} antes</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {planTier === 'casal' && (
        <div className="bc-account-settings__row" style={{ marginTop: '1rem' }}>
          <label className="bc-account-settings__item-desc">Alertas no WhatsApp</label>
          {!hasWhatsApp ? (
            <span className="bc-account-settings__item-desc" style={{ fontSize: '12px' }}>
              <a href="#whatsapp-bot">Cadastre seu número</a> para ativar
            </span>
          ) : (
            <input
              type="checkbox"
              checked={prefs.whatsapp_notify}
              onChange={e => handleToggleWhatsApp(e.target.checked)}
            />
          )}
        </div>
      )}

      <p className="bc-account-settings__item-desc" style={{ marginTop: '1rem', fontSize: '12px' }}>
        Notificações push em iOS requerem iOS 16.4+ e o app adicionado à tela inicial.
      </p>
    </div>
  )
}
